import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Save, Plus, Search, Edit, Trash2, X } from "lucide-react";
import { HeaderPage } from "@/components/HeaderPage";
import { TextArea } from "@/components/TextArea";
import { Loading } from "@/components/Loading";
import { motion } from "framer-motion";
import { Controller, useForm } from "react-hook-form";
import {
  updateSectorSchema,
  type UpdateSectorFormData,
} from "@/schemas/sectorSchemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useParams } from "react-router-dom";
import { useGetSectorById, useUpdateSector } from "@/hooks/useSectors";
import {
  useCreateRegional,
  useGetRegionals,
  useUpdateRegional,
  useDeleteRegional,
} from "@/hooks/useRegionals";
import { useGetUsers } from "@/hooks/useUsers";
import { useDebounce } from "@/hooks/useDebounce";
import toast from "react-hot-toast";
import { useEffect, useState } from "react";
import { applyPhoneMask, removePhoneMask } from "@/lib/utils";
import type { Regional } from "@/types/Regional";
import type { CreateRegionalDto } from "@/dtos/CreateRegionalDto";
import { Badge } from "@/components/ui/badge";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useQueryClient } from "@tanstack/react-query";

export const EditSector = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const { data: sector, isLoading } = useGetSectorById(id!);
  const updateSector = useUpdateSector();
  const [userSearch, setUserSearch] = useState("");
  const debouncedSearch = useDebounce(userSearch, 500);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);

  // Regionais hooks
  const { data: regionalsData } = useGetRegionals(id!);
  const createRegional = useCreateRegional();
  const updateRegional = useUpdateRegional();
  const deleteRegional = useDeleteRegional();

  // Estados para regionais
  const [showRegionalForm, setShowRegionalForm] = useState(false);
  const [editingRegional, setEditingRegional] = useState<Regional | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [regionalToDelete, setRegionalToDelete] = useState<string | null>(null);
  const [searchResponsible, setSearchResponsible] = useState("");

  // Estados do formulário de regional
  const [regionalForm, setRegionalForm] = useState<CreateRegionalDto>({
    name: "",
    description: "",
    responsibleId: "",
    email: "",
    neighborhoods: [],
    isActive: true,
  });
  const [newNeighborhood, setNewNeighborhood] = useState("");

  // Buscar usuários com roles específicas e search com debounce
  const { data: usersData } = useGetUsers({
    search: debouncedSearch,
    role: "ADMIN,AGENT,TRIAGE",
  });

  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { errors },
  } = useForm<UpdateSectorFormData>({
    resolver: zodResolver(updateSectorSchema),
    defaultValues: {
      status: "ACTIVE",
      hasRegionals: false,
    },
  });

  useEffect(() => {
    if (sector) {
      reset({
        name: sector.name,
        acronym: sector.acronym,
        description: sector.description,
        email: sector.email,
        phone: applyPhoneMask(sector.phone),
        workingHoursStart: sector.workingHoursStart,
        workingHoursEnd: sector.workingHoursEnd,
        responsibleId: sector.responsibleId || undefined,
        status: sector.status,
        hasRegionals: sector.hasRegionals,
      });

      // Carregar usuário responsável atual
      if (sector.responsible) {
        setSelectedUser(sector.responsible);
      }
    }
  }, [sector, reset]);

  const onSubmitUpdate = async (data: UpdateSectorFormData) => {
    await updateSector.mutateAsync(
      {
        id: id!,
        name: data.name,
        acronym: data.acronym,
        description: data.description,
        email: data.email,
        phone: removePhoneMask(data.phone),
        workingHoursStart: data.workingHoursStart,
        workingHoursEnd: data.workingHoursEnd,
        responsibleId: data.responsibleId || undefined,
        status: data.status,
        hasRegionals: data.hasRegionals,
      },
      {
        onSuccess: () => {
          navigate("/sectors");
          toast.success("Setor atualizado com sucesso!");
        },
        onError: (error: any) => {
          const errorMessage =
            error.response?.data?.message || "Erro ao atualizar setor";
          toast.error(errorMessage);
        },
      },
    );
  };

  // Funções para gerenciar regionais
  const handleSaveRegional = async () => {
    if (!regionalForm.name.trim()) {
      toast.error("Nome da regional é obrigatório");
      return;
    }

    if (regionalForm.neighborhoods.length === 0) {
      toast.error("Adicione pelo menos um bairro");
      return;
    }

    try {
      if (editingRegional) {
        await updateRegional.mutateAsync({
          sectorId: id!,
          regionalId: editingRegional.id,
          data: regionalForm,
        });
        toast.success("Regional atualizada com sucesso!");
      } else {
        await createRegional.mutateAsync({
          sectorId: id!,
          data: regionalForm,
        });
        toast.success("Regional criada com sucesso!");
      }

      queryClient.invalidateQueries({ queryKey: ["getRegionals", id] });
      resetRegionalForm();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        `Erro ao ${editingRegional ? "atualizar" : "criar"} regional`;
      toast.error(errorMessage);
    }
  };

  const handleEditRegional = (regional: Regional) => {
    setEditingRegional(regional);
    setRegionalForm({
      name: regional.name,
      description: regional.description || "",
      responsibleId: regional.responsibleId || "",
      email: regional.email || "",
      neighborhoods: regional.neighborhoods,
      isActive: regional.isActive,
    });
    setShowRegionalForm(true);
  };

  const handleDeleteRegional = async () => {
    if (!regionalToDelete) return;

    try {
      await deleteRegional.mutateAsync({
        regionalId: regionalToDelete,
      });
      toast.success("Regional excluída com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["getRegionals", id] });
      setDeleteConfirmOpen(false);
      setRegionalToDelete(null);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Erro ao excluir regional";
      toast.error(errorMessage);
    }
  };

  const resetRegionalForm = () => {
    setRegionalForm({
      name: "",
      description: "",
      responsibleId: "",
      email: "",
      neighborhoods: [],
      isActive: true,
    });
    setNewNeighborhood("");
    setShowRegionalForm(false);
    setEditingRegional(null);
  };

  const handleAddNeighborhood = () => {
    if (!newNeighborhood.trim()) return;

    setRegionalForm({
      ...regionalForm,
      neighborhoods: [...regionalForm.neighborhoods, newNeighborhood.trim()],
    });
    setNewNeighborhood("");
  };

  const handleRemoveNeighborhood = (index: number) => {
    setRegionalForm({
      ...regionalForm,
      neighborhoods: regionalForm.neighborhoods.filter((_, i) => i !== index),
    });
  };

  const filteredUsers = usersData?.data?.filter((user: any) => {
    const fullName = user.profile?.fullName || "";
    const email = user.email || "";
    return (
      fullName.toLowerCase().includes(searchResponsible.toLowerCase()) ||
      email.toLowerCase().includes(searchResponsible.toLowerCase())
    );
  });

  if (isLoading) {
    return <Loading label="Carregando setor..." />;
  }

  if (!sector) {
    return (
      <div>
        <HeaderPage
          title="Editar setor"
          description="Informe novos dados para o setor selecionado"
        />
        <div className="flex-1 overflow-y-auto mt-10">
          <Card className="bg-white">
            <CardContentUI className="p-6">
              <p className="text-center text-gray-500">Setor não encontrado</p>
            </CardContentUI>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <HeaderPage
        title="Atualizar setor"
        description="Informe novos dados para o setor selecionado"
      />

      <div className="flex-1 overflow-y-auto mt-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Tabs defaultValue="dados" className="space-y-4">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="dados">Dados do Setor</TabsTrigger>
              <TabsTrigger value="regionais">Regionais</TabsTrigger>
            </TabsList>

            {/* Aba: Dados do Setor */}
            <TabsContent value="dados">
              <Card className="bg-white">
                <CardContentUI className="p-6">
                  <form
                    onSubmit={handleSubmit(onSubmitUpdate)}
                    className="space-y-6"
                  >
                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Informações Básicas
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          title="Nome do Setor"
                          isRequired
                          placeholder="Ex: Secretaria de Obras"
                          error={errors.name?.message}
                          {...register("name")}
                        />

                        <Input
                          title="Sigla"
                          isRequired
                          placeholder="Ex: SEMOB"
                          error={errors.acronym?.message}
                          {...register("acronym")}
                        />
                      </div>

                      <div className="mt-4">
                        <TextArea
                          title="Descrição"
                          isRequired
                          placeholder="Descreva as responsabilidades do setor"
                          {...register("description")}
                        />
                        {errors.description && (
                          <p className="text-sm text-red-500 mt-1">
                            {errors.description.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Informações de Contato */}
                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Informações de Contato
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          title="Email"
                          isRequired
                          type="email"
                          placeholder="setor@municipio.gov.br"
                          error={errors.email?.message}
                          {...register("email")}
                        />

                        <Controller
                          name="phone"
                          control={control}
                          render={({ field }) => (
                            <Input
                              title="Telefone"
                              isRequired
                              placeholder="(11) 91234-5678"
                              error={errors.phone?.message}
                              value={field.value}
                              onChange={(e) => {
                                const maskedValue = applyPhoneMask(
                                  e.target.value,
                                );
                                field.onChange(maskedValue);
                              }}
                            />
                          )}
                        />
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Horário de Funcionamento
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          title="Horário de Início"
                          isRequired
                          type="time"
                          error={errors.workingHoursStart?.message}
                          {...register("workingHoursStart")}
                        />

                        <Input
                          title="Horário de Término"
                          isRequired
                          type="time"
                          error={errors.workingHoursEnd?.message}
                          {...register("workingHoursEnd")}
                        />
                      </div>
                    </div>

                    {/* Configurações */}
                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Configurações
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Responsável */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium">
                            Responsável <span className="text-red-500">*</span>
                          </label>
                          <Controller
                            name="responsibleId"
                            control={control}
                            render={({ field }) => (
                              <div className="relative">
                                <div className="relative">
                                  <Input
                                    placeholder="Buscar responsável..."
                                    value={
                                      selectedUser
                                        ? selectedUser.profile?.fullName
                                        : userSearch
                                    }
                                    onChange={(e) => {
                                      setUserSearch(e.target.value);
                                      setSelectedUser(null);
                                      field.onChange("");
                                      setShowDropdown(true);
                                    }}
                                    onFocus={() => setShowDropdown(true)}
                                    error={errors.responsibleId?.message}
                                  />
                                  {selectedUser && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSelectedUser(null);
                                        setUserSearch("");
                                        field.onChange("");
                                      }}
                                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>

                                {showDropdown &&
                                  !selectedUser &&
                                  usersData?.data &&
                                  usersData.data.length > 0 && (
                                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-[280px] overflow-y-auto">
                                      {usersData.data.map((user: any) => (
                                        <div
                                          key={user.id}
                                          onClick={() => {
                                            setSelectedUser(user);
                                            field.onChange(user.id);
                                            setShowDropdown(false);
                                            setUserSearch("");
                                          }}
                                          className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                                        >
                                          <div className="flex flex-col">
                                            <span className="font-medium text-sm">
                                              {user.profile?.fullName}
                                            </span>
                                            <span className="text-xs text-gray-500">
                                              {user.email} •{" "}
                                              {user.role === "ADMIN"
                                                ? "Administrador"
                                                : user.role === "AGENT"
                                                  ? "Agente"
                                                  : "Triagem"}
                                            </span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                {showDropdown &&
                                  !selectedUser &&
                                  debouncedSearch &&
                                  usersData?.data?.length === 0 && (
                                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                                      <div className="px-4 py-6 text-center text-sm text-gray-500">
                                        Nenhum usuário encontrado
                                      </div>
                                    </div>
                                  )}
                              </div>
                            )}
                          />
                          {errors.responsibleId && (
                            <p className="text-sm text-red-500">
                              {errors.responsibleId.message}
                            </p>
                          )}
                        </div>

                        <Controller
                          name="status"
                          control={control}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <SelectTrigger title="Status" isRequired>
                                <SelectValue placeholder="Selecione o status" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="ACTIVE">Ativo</SelectItem>
                                <SelectItem value="INACTIVE">
                                  Inativo
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        />
                        {errors.status && (
                          <p className="text-sm text-red-500 col-span-2">
                            {errors.status.message}
                          </p>
                        )}

                        <Controller
                          name="hasRegionals"
                          control={control}
                          render={({ field }) => (
                            <Select
                              onValueChange={(value) =>
                                field.onChange(value === "true")
                              }
                              value={field.value ? "true" : "false"}
                            >
                              <SelectTrigger title="Possui Regionais">
                                <SelectValue placeholder="Selecione" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="false">Não</SelectItem>
                                <SelectItem value="true">Sim</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </div>
                    </div>
                    <div className="flex gap-3 pt-4 border-t">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate("/sectors")}
                        disabled={updateSector.isPending}
                        className="cursor-pointer"
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Voltar
                      </Button>
                      <Button
                        type="submit"
                        disabled={updateSector.isPending}
                        isLoading={updateSector.isPending}
                        className="text-white cursor-pointer"
                      >
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Atualizar
                        </>
                      </Button>
                    </div>
                  </form>
                </CardContentUI>
              </Card>
            </TabsContent>

            {/* Aba: Regionais */}
            <TabsContent value="regionais">
              <Card className="bg-white">
                <CardContentUI className="p-6">
                  <div className="space-y-6">
                    {/* Cabeçalho */}
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-lg font-semibold">
                          Gerenciar Regionais
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Organize o setor em regionais por bairros
                        </p>
                      </div>
                      {!showRegionalForm && (
                        <Button
                          type="button"
                          onClick={() => setShowRegionalForm(true)}
                          className="text-white cursor-pointer"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Nova Regional
                        </Button>
                      )}
                    </div>

                    {/* Formulário de Regional */}
                    {showRegionalForm && (
                      <Card className="border-2 border-primary/20 bg-blue-50/30">
                        <CardContentUI className="p-4 space-y-4">
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="font-semibold">
                              {editingRegional
                                ? "Editar Regional"
                                : "Nova Regional"}
                            </h4>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={resetRegionalForm}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Nome */}
                            <div className="space-y-2">
                              <label className="text-sm font-medium">
                                Nome da Regional{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <Input
                                placeholder="Ex: Regional Norte"
                                value={regionalForm.name}
                                onChange={(e) =>
                                  setRegionalForm({
                                    ...regionalForm,
                                    name: e.target.value,
                                  })
                                }
                              />
                            </div>

                            {/* Email */}
                            <div className="space-y-2">
                              <label className="text-sm font-medium">
                                Email
                              </label>
                              <Input
                                type="email"
                                placeholder="regional.norte@municipio.gov.br"
                                value={regionalForm.email}
                                onChange={(e) =>
                                  setRegionalForm({
                                    ...regionalForm,
                                    email: e.target.value,
                                  })
                                }
                              />
                            </div>

                            {/* Responsável */}
                            <div className="space-y-2">
                              <label className="text-sm font-medium">
                                Responsável
                              </label>
                              <Select
                                onValueChange={(value) =>
                                  setRegionalForm({
                                    ...regionalForm,
                                    responsibleId: value,
                                  })
                                }
                                value={regionalForm.responsibleId}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione o responsável" />
                                </SelectTrigger>
                                <SelectContent>
                                  <div className="flex items-center border-b px-3 pb-2">
                                    <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                                    <Input
                                      placeholder="Pesquisar responsável..."
                                      value={searchResponsible}
                                      onChange={(e) =>
                                        setSearchResponsible(e.target.value)
                                      }
                                      className="h-8 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                                    />
                                  </div>
                                  <div className="max-h-[200px] overflow-y-auto">
                                    {filteredUsers?.map((user: any) => (
                                      <SelectItem key={user.id} value={user.id}>
                                        {user.profile?.fullName || user.email}
                                      </SelectItem>
                                    ))}
                                    {filteredUsers?.length === 0 && (
                                      <div className="py-6 text-center text-sm text-gray-500">
                                        Nenhum usuário encontrado
                                      </div>
                                    )}
                                  </div>
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Status */}
                            <div className="space-y-2">
                              <label className="text-sm font-medium">
                                Status
                              </label>
                              <Select
                                onValueChange={(value) =>
                                  setRegionalForm({
                                    ...regionalForm,
                                    isActive: value === "true",
                                  })
                                }
                                value={regionalForm.isActive ? "true" : "false"}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="true">Ativa</SelectItem>
                                  <SelectItem value="false">Inativa</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          {/* Descrição */}
                          <div className="space-y-2">
                            <label className="text-sm font-medium">
                              Descrição
                            </label>
                            <TextArea
                              placeholder="Descreva as responsabilidades da regional"
                              value={regionalForm.description}
                              onChange={(e) =>
                                setRegionalForm({
                                  ...regionalForm,
                                  description: e.target.value,
                                })
                              }
                            />
                          </div>

                          {/* Bairros */}
                          <div className="space-y-2">
                            <label className="text-sm font-medium">
                              Bairros Atendidos{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <div className="flex gap-2">
                              <Input
                                placeholder="Digite o nome do bairro"
                                value={newNeighborhood}
                                onChange={(e) =>
                                  setNewNeighborhood(e.target.value)
                                }
                                onKeyPress={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleAddNeighborhood();
                                  }
                                }}
                              />
                              <Button
                                type="button"
                                onClick={handleAddNeighborhood}
                                disabled={!newNeighborhood.trim()}
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                            {regionalForm.neighborhoods.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {regionalForm.neighborhoods.map(
                                  (neighborhood, index) => (
                                    <Badge
                                      key={index}
                                      variant="secondary"
                                      className="cursor-pointer"
                                    >
                                      {neighborhood}
                                      <X
                                        className="h-3 w-3 ml-1"
                                        onClick={() =>
                                          handleRemoveNeighborhood(index)
                                        }
                                      />
                                    </Badge>
                                  ),
                                )}
                              </div>
                            )}
                          </div>

                          {/* Botões */}
                          <div className="flex gap-2 pt-4 border-t">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={resetRegionalForm}
                              className="cursor-pointer"
                            >
                              Cancelar
                            </Button>
                            <Button
                              type="button"
                              onClick={handleSaveRegional}
                              disabled={
                                createRegional.isPending ||
                                updateRegional.isPending
                              }
                              className="text-white cursor-pointer"
                            >
                              <Save className="w-4 h-4 mr-2" />
                              {editingRegional ? "Atualizar" : "Salvar"}
                            </Button>
                          </div>
                        </CardContentUI>
                      </Card>
                    )}

                    {/* Lista de Regionais */}
                    <div className="space-y-3">
                      {regionalsData?.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <p>Nenhuma regional cadastrada</p>
                        </div>
                      ) : (
                        regionalsData?.map((regional: Regional) => (
                          <Card key={regional.id}>
                            <CardContentUI className="p-4">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <h4 className="font-semibold">
                                      {regional.name}
                                    </h4>
                                    <Badge
                                      variant={
                                        regional.isActive
                                          ? "default"
                                          : "secondary"
                                      }
                                      className="pointer-events-none"
                                    >
                                      {regional.isActive ? "Ativa" : "Inativa"}
                                    </Badge>
                                  </div>
                                  {regional.description && (
                                    <p className="text-sm text-muted-foreground mb-2">
                                      {regional.description}
                                    </p>
                                  )}
                                  <div className="grid grid-cols-2 gap-2 text-sm">
                                    {regional.responsible && (
                                      <div>
                                        <span className="text-muted-foreground">
                                          Responsável:
                                        </span>{" "}
                                        {regional.responsible.profile
                                          ?.fullName ||
                                          regional.responsible.email}
                                      </div>
                                    )}
                                    {regional.email && (
                                      <div>
                                        <span className="text-muted-foreground">
                                          Email:
                                        </span>{" "}
                                        {regional.email}
                                      </div>
                                    )}
                                  </div>
                                  <div className="mt-2">
                                    <span className="text-sm text-muted-foreground">
                                      Bairros:
                                    </span>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {regional.neighborhoods.map(
                                        (neighborhood, index) => (
                                          <Badge
                                            key={index}
                                            variant="outline"
                                            className="text-xs pointer-events-none"
                                          >
                                            {neighborhood}
                                          </Badge>
                                        ),
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleEditRegional(regional)}
                                    className="cursor-pointer"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setRegionalToDelete(regional.id);
                                      setDeleteConfirmOpen(true);
                                    }}
                                    className="cursor-pointer text-red-600 hover:text-red-700"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </CardContentUI>
                          </Card>
                        ))
                      )}
                    </div>
                  </div>
                </CardContentUI>
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Excluir Regional"
        description="Tem certeza que deseja excluir esta regional? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={handleDeleteRegional}
        variant="destructive"
        isLoading={deleteRegional.isPending}
      />
    </div>
  );
};
